import { Link } from "@tanstack/react-router";
import { forwardRef, useMemo, useState } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import CommunityScoreWheel from "@/components/community-score-wheel";
import GameCategories from "@/components/game-categories";
import {
  CompletedIcon,
  DislikeIcon,
  LikeIcon,
  LoveIcon,
  SavedIcon,
} from "@/components/game-list-image/icons";
import StatusBox from "@/components/game-list-image/status-box";
import UserActions from "@/components/game-list-image/user-actions";
import { CouponsIcon } from "@/components/icon-stack/icons";
import { AwardIcon } from "@/components/icons/award-icon";
import RatingSteps, { RatingStepsEnum } from "@/components/played-button-alt";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { EscapeType } from "@/graphql/mutations/use-update-escape";
import { toSentenceCase } from "@/helpers/formatters";
import { gameUrl } from "@/helpers/games";
import { cn } from "@/lib/utils";
import type { GameType } from "@/pages/company/types";
import useGameStatus from "@/pages/search/hooks/use-game-status";
import { OverallRatingEnum } from "@/pages/search/types";

import ComingSoon from "./coming-soon";

interface ScoreTooltipProps {
  ratings: number;
}

function ScoreTooltip({ ratings }: ScoreTooltipProps) {
  return <span className="text-14">{ratings} Ratings</span>;
}

interface CardProps extends React.ComponentProps<"div"> {
  className?: string;
  game: GameType;
  iconClassName?: string;
}

export default forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, game, iconClassName, ...props },
  ref
) {
  const [userEscape, setUserEscape] = useState<EscapeType | undefined>(
    undefined
  );
  const [currentRatingStep, setCurrentRatingStep] = useState<
    (typeof RatingStepsEnum)[keyof typeof RatingStepsEnum]
  >(RatingStepsEnum.DEFAULT);

  const { isOpen } = useGameStatus(game);

  const handleGameCompleted = (escapeObj: EscapeType) => {
    setUserEscape(escapeObj);
    setCurrentRatingStep(RatingStepsEnum.RATING_STEP);
  };

  const isDefaultRatingStep = useMemo(
    () => currentRatingStep === RatingStepsEnum.DEFAULT,
    [currentRatingStep]
  );

  const RatingIcon = useMemo(() => {
    if (game.overallRating === OverallRatingEnum.DISLIKE) {
      return DislikeIcon;
    }
    if (game.overallRating === OverallRatingEnum.LIKE) {
      return LikeIcon;
    }
    if (game.overallRating === OverallRatingEnum.LOVE) {
      return LoveIcon;
    }
    return null;
  }, [game.overallRating]);

  const statusIcon = useMemo(() => {
    if (!!game.isSaved && !game.isCompleted) {
      return (
        <StatusBox
          className="z-1 bg-linear-180 from-rose-red to-plum"
          variant="large"
        >
          <SavedIcon className="aspect-[5/9] h-auto w-1/3" />
        </StatusBox>
      );
    }

    if (RatingIcon) {
      return (
        <StatusBox className="z-1" rating={game.overallRating} variant="large">
          <RatingIcon className="aspect-square h-auto w-6/10" />
        </StatusBox>
      );
    }

    return null;
  }, [game, RatingIcon]);

  const awardIcon = useMemo(
    () =>
      !!game.awards && game.awards.length > 0 ? (
        <AwardIcon
          className={cn("absolute top-0 left-3.5 z-0", iconClassName)}
        />
      ) : null,
    [game, iconClassName]
  );

  const hasCoupon = useMemo(() => !!game.coupons?.length, [game.coupons]);

  return (
    <div
      className={cn("group relative cursor-pointer", className)}
      ref={ref}
      {...props}
    >
      <div
        className={cn(
          "relative overflow-hidden rounded-[10px]",
          !game.isCompleted &&
            'after:absolute after:inset-0 after:bg-light-black/60 after:opacity-0 after:backdrop-blur-[2px] after:transition-opacity after:content-[""] group-hover:after:opacity-100'
        )}
      >
        <CloudinaryImage
          className="aspect-square w-full"
          game={game}
          rW={576}
        />
        <ComingSoon game={game} />
        {isDefaultRatingStep && (
          <>
            {game.isCompleted && (
              <div className="absolute inset-0 flex items-center justify-center bg-linear-68 from-20% from-mint-turquoise/40 to-60% to-bright-turquoise/40">
                <CompletedIcon className="size-20" />
              </div>
            )}
            <UserActions
              game={
                game as unknown as import("@/components/game-list-image/user-actions").UserGameType
              }
              onGameCompleted={handleGameCompleted}
            />
          </>
        )}
        {!statusIcon && awardIcon}
        {!isDefaultRatingStep && (
          <div className="absolute inset-0 z-2 flex items-center rounded-[10px] bg-light-black/80 p-5 backdrop-blur-[10px]">
            <RatingSteps
              currentRatingStep={currentRatingStep}
              game={{ ...game, myEscape: userEscape }}
              setCurrentRatingStep={setCurrentRatingStep}
            />
          </div>
        )}
      </div>
      {isDefaultRatingStep && statusIcon}
      <div className="flex flex-col items-start gap-2.5 pt-3">
        <h2
          className={cn(
            "font-semibold text-16",
            isOpen ? "text-white" : "text-pale-lavender"
          )}
        >
          <Link className="link-overlay hover:underline" to={gameUrl(game)}>
            {game.name}
            {!isOpen && " - CLOSED"}
            {isOpen && hasCoupon && <CouponsIcon className="ml-[7px] w-4.5" />}
          </Link>
        </h2>
        {!!game.communityScorePercentages && !!game.communityScoreBucket && (
          <Tooltip>
            <TooltipTrigger className="relative flex items-center gap-1.5">
              <CommunityScoreWheel
                className={cn("size-4", iconClassName)}
                scores={game.communityScorePercentages}
              />
              <span className="font-medium text-14">
                {toSentenceCase(game.communityScoreBucket)}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <ScoreTooltip ratings={game.communityRatingCount || 0} />
            </TooltipContent>
          </Tooltip>
        )}
        {game.primaryCategory && (
          <GameCategories
            categories={[game.primaryCategory, ...game.secondaryCategories]}
            className="gap-1.5"
          />
        )}
      </div>
    </div>
  );
});
